/**
 * Xdm Commission Rate service
 */
import { Injectable, Injector } from '@angular/core';
import { ColDef, GridApi } from 'ag-grid-community';

import { BaseInjectService } from '../base';
import {
  ENTITY_TYPES,
  XuxGridAction,
  XUX_GRID_ACTIONS,
  IDMCommissionRate,
  IFilterQuery,
  IProduct,
  ObjectId,
  IBaseEntityType,
  IWidgetAction,
  EDIT_GRID_ITEM_ACTION_TYPE,
} from '@shared/types';
import { XuxGridListComponent } from '@app/components/xux/xux-grid-list/xux-grid-list-widget.component';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from 'environments/environment';
import {
  chunk,
  forEach,
  get,
  isEmpty,
  join,
  map,
  reduce,
  set,
  size,
  sortBy,
  uniqBy,
  unset,
} from 'lodash';
import { lazyInject } from '@app/containers/base/decorators';
import { ProductService } from '../product';
import {
  batchRequestMap,
  defaultBatchMapAccumulatedPredict,
} from '@app/shared/rx-operators';
import { ITreeData } from '@app/shared/types/common/tree.type';
import { FormGroup } from '@angular/forms';
import { I18n } from '@app/i18n/strings-keys';
import { DecimalNumber } from '@app/shared/common/decimal-number';
import { XdmCommissionRateService } from '../xdm-commission-rate';
import {
  getAllRowsNodeFromGrid,
  getSortedTreeDataByConfig,
} from '@app/shared/utils';
import { observableOf, rx } from '@app/shared/rx';
import { DEFAULT_PAGE_SIZE } from '@config/index';
import {
  MajorReportingCategoryPath,
  MinorReportingCategoryPath,
} from '@app/shared/constants';

/**
 * XVI Commission Rate service to display commission rate details
 */
@Injectable()
export class XviCommissionRateService extends BaseInjectService {
  @lazyInject(ProductService) public productService: ProductService;
  @lazyInject(XdmCommissionRateService)
  public xdmCommissionRateService: XdmCommissionRateService;

  public quickFilterFields: string[] = ['name'];

  public listProjection: string[] = ['name'];

  public headerNames: ColDef[] = [
    {
      headerName: 'Name',
      field: 'name',
      filter: false,
    },
  ];
  public isExpandAllProducts$: BehaviorSubject<string> =
    new BehaviorSubject<string>('');
  public commissionRatesDescription$: BehaviorSubject<string> =
    new BehaviorSubject<string>('');
  public commissionRatesGridSubject: Subject<IWidgetAction> = new Subject();
  public isCommissionRatesGridBusy$: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  public isCollapsed: boolean = true;
  public listGridApi: GridApi;
  public form: FormGroup;

  private get baseUrl(): string {
    return environment.dmBackendURI;
  }

  public constructor(public injector: Injector) {
    super(injector);
  }

  public fetchCommissionRateDetails(documentId: string): Observable<{
    productToTree?: ITreeData;
    tags?: IBaseEntityType[];
    name?: string;
  }> {
    return this.xdmCommissionRateService
      .fetchList({
        filter: `entity_id eq in(${documentId}) and is_active eq true`,
        select: ['name', 'commission_rates'],
      })
      .pipe(
        rx.map((response) => get(response, 'items[0]', null)),
        rx.switchMap((commissionRateResponse: IDMCommissionRate) => {
          const productIds =
            commissionRateResponse?.commission_rates?.map(
              (p) => p.product_entity_id,
            ) || [];
          const query: IFilterQuery = {
            include_inactive: false,
            include_entities: [
              ENTITY_TYPES.PRODUCT,
              ENTITY_TYPES.TAG,
              ENTITY_TYPES.REPORTING_CATEGORY,
            ],
            select: [
              'name',
              'product_id',
              'tag_entity_ids',
              'product_tag_entity_ids',
              'tags.name',
              'reporting_category',
              MinorReportingCategoryPath,
              MajorReportingCategoryPath,
            ],
          };
          if (isEmpty(productIds)) {
            return observableOf({
              productToTree: [],
              tags: [],
              name: commissionRateResponse.name,
            });
          }

          return batchRequestMap(
            chunk(productIds, DEFAULT_PAGE_SIZE),
            (chunkProductIds) => {
              return this.productService.fetchList({
                ...query,
                filter: `entity_id eq in(${join(chunkProductIds, ',')})`,
              });
            },
          ).pipe(
            rx.reduce(defaultBatchMapAccumulatedPredict, {}),
            rx.map((productsResponse) => {
              const products = get(productsResponse, 'items', []);
              const rateMaps = reduce(
                get(commissionRateResponse, 'commission_rates', []),
                (rateMap, rate) => {
                  rateMap[rate.product_entity_id] = rate.rate;

                  return rateMap;
                },
                {},
              );

              forEach(products, (product) => {
                set(
                  product,
                  'rate',
                  new DecimalNumber(
                    get(rateMaps, get(product, 'entity_id'), null),
                  )
                    .mul(100)
                    .valueOf(),
                );
                unset(product, 'nutritional_data');
              });

              const productsWithTags = this.initProductsForTag(
                products as IProduct[],
              );

              const tags = [];
              forEach(productsWithTags, (product) => {
                const productTags = get(product, 'tags', []);
                tags.push(...productTags);
              });

              const productToTree =
                this.convertProductsToTree(productsWithTags);

              return {
                productToTree,
                tags: map(sortBy(uniqBy(tags, 'entity_id'), 'name'), (tag) => ({
                  ...tag,
                  text: tag.name,
                  value: tag.entity_id,
                })),
                name: commissionRateResponse.name,
              };
            }),
          );
        }),
      );
  }

  public listModalGridAction(
    event: XuxGridAction,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    grid: XuxGridListComponent,
  ): void {
    this.listGridApi = event.payload?.gridApi;
  }

  public initProductsForTag(products: IProduct[]): IProduct[] {
    if (isEmpty(products)) return [];

    return map(products, (product: IProduct) => {
      if (!isEmpty(product.product_tag_entity_ids)) {
        const productTagEntityIds: ObjectId[] = get(
          product,
          'product_tag_entity_ids',
          [],
        );
        set(product, 'product_tag_ids', productTagEntityIds);
      }
      if (!isEmpty(product.tag_entity_ids)) {
        const modifierTagEntityIds: ObjectId[] = get(
          product,
          'tag_entity_ids',
          [],
        );
        set(product, 'product_tag_ids', modifierTagEntityIds);
      }

      return product;
    });
  }

  public convertProductsToTree(items: IProduct[]): ITreeData[] {
    return getSortedTreeDataByConfig(items, {
      path: MajorReportingCategoryPath,
      groupName: 'name',
      additionalTreeFn: (name): IBaseEntityType => ({
        isMajorCategoryRow: true,
        reporting_category_major: {
          name,
        },
      }),
      childrenConfig: {
        path: MinorReportingCategoryPath,
        additionalTreeFn: (name): IBaseEntityType => ({
          isMinorCategoryRow: true,
          reporting_category_minor: {
            name,
          },
        }),
        groupName: 'name',
        childrenConfig: {
          path: 'name',
          secondarySortPath: 'product_id',
        },
      },
    });
  }

  public expandAllProductsClick(): void {
    this.isCollapsed = !this.isCollapsed;
    this.listGridApi?.forEachNode((node) => {
      node.setExpanded(
        !this.isCollapsed || get(node, 'data.isMajorCategoryRow', false),
      );
    });
    this.isExpandAllProducts$.next(
      this.i18nService.translate(
        I18n.Forms.HawkerProfileForm.OperateAllProducts,
        {
          action: this.i18nService.translate(
            this.isCollapsed ? I18n.Common.Expand : I18n.Common.Collapse,
          ),
        },
      ),
    );

    this.listGridApi.ensureIndexVisible(0, 'top');
  }

  public async onGridAction(action: IWidgetAction): Promise<void> {
    const actionType: XUX_GRID_ACTIONS | EDIT_GRID_ITEM_ACTION_TYPE =
      action?.type;

    switch (actionType) {
      case XUX_GRID_ACTIONS.QUICK_FILTER_CHANGED:
      case XUX_GRID_ACTIONS.FILTER_MODEL_CHANGED:
      case XUX_GRID_ACTIONS.GRID_READY:
      case XUX_GRID_ACTIONS.ITEMS_ARRAY_CHANGED:
        this.handleCommissionRatesDescription();
        break;
      default:
        break;
    }
  }

  public handleCommissionRatesDescription(): void {
    const count = size(this.getDisplayProductIds(true));

    this.commissionRatesDescription$.next(
      this.i18nService.instant(I18n.Forms.GridMessages.ShowingEntities, {
        count,
        entities: this.i18nService.instant(
          I18n.EntityNames.ProductSingularAndPlural,
        ),
      }),
    );
  }

  public getDisplayProductIds(isLeaf?: boolean): string[] {
    return map(getAllRowsNodeFromGrid(this.listGridApi, isLeaf), 'id');
  }

  public initCommissionRatesGridActionSubscription(): void {
    this.subscriptions.push(
      this.commissionRatesGridSubject.subscribe((action) =>
        this.onGridAction(action),
      ),
    );
  }
}
